import {GetAnalysisPipelineStep, GetAnalysisChart} from 'analysis-tool/AnalysisTypes';
import {assert, assertAndContinue} from 'Asserts';
import {isInaccessible, assertIsAccessible} from 'graphql-client/InaccessibleInfo';

/** @package */
export const MISSING_STEP_DATA_ERROR = 'Expected step to have step data with name';
/** @package */
export const NO_CHARTS_ERROR = 'Expected step data to have at least one chart';
/** @package */
export const TOO_MANY_CHARTS_ERROR = 'Expected step data to only have one chart';

/**
 * Given a step and a step data name, assert the presence of the step data and its charts as either input
 * or output step data and return its charts.
 */
export function findAndAssertStepDataCharts(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean,
  isOptional: boolean = false
): Array<GetAnalysisChart> {
  const stepData = isLookingForInputStepData
    ? step.inputStepDatas.find(({name}) => name === stepDataName)
    : step.outputStepDatas.find(({name}) => name === stepDataName);

  assert(stepData != null, MISSING_STEP_DATA_ERROR, {isLookingForInputStepData, step, stepDataName});
  assert(isOptional || stepData.charts.length >= 1, NO_CHARTS_ERROR, {
    isLookingForInputStepData,
    step,
    stepDataName,
  });

  return stepData.charts.filter((chart) => !isInaccessible(chart)).map((chart) => assertIsAccessible(chart));
}

/**
 * Given a step and a step data name, assert the presence of the step data as either an input or output step data
 * that should only have one chart and return the chart.
 *
 * The assumption of having 1 chart is checked as a WARNING instead of an ERROR, so that the application
 * doesn't fail with a hard error.
 */
export function findAndAssertSingleStepChart(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean = false
): GetAnalysisChart {
  const charts = findAndAssertStepDataCharts(step, stepDataName, isLookingForInputStepData);
  assertAndContinue(charts.length === 1, TOO_MANY_CHARTS_ERROR, {
    step,
    stepDataName,
    isLookingForInputStepData,
  });

  return charts[0];
}
